import React from 'react';
import { Typography, theme } from 'antd';
import { TranslationOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { PaperWorkLanguage } from '../Types/interfaces';

interface PaperWorkLanguageNoticeProps {
  /** Enum value (0/1) or enum name ('Romanian'/'English'); renders nothing while unknown. */
  language?: PaperWorkLanguage | string | null;
  /** 'form' for documents filled in by students/tutors, 'organization' for the registration form. */
  variant?: 'form' | 'organization';
  style?: React.CSSProperties;
}

const toLanguageName = (
  language: PaperWorkLanguage | string | null | undefined
): keyof typeof PaperWorkLanguage | undefined => {
  if (language === undefined || language === null) return undefined;
  const name =
    typeof language === 'number' ? PaperWorkLanguage[language] : language;
  return name in PaperWorkLanguage
    ? (name as keyof typeof PaperWorkLanguage)
    : undefined;
};

/** Tells the user in which language a form has to be filled in (the organization's paperwork language). */
export const PaperWorkLanguageNotice = ({
  language,
  variant = 'form',
  style,
}: PaperWorkLanguageNoticeProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const name = toLanguageName(language);
  if (!name) return null;

  const languageLabel = t(`orgRegComp.paperWorkLanguages.${name}`);

  return (
    <div
      role="note"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '12px 16px',
        borderRadius: token.borderRadiusLG,
        background: token.colorInfoBg,
        border: `1px solid ${token.colorInfoBorder}`,
        borderLeft: `4px solid ${token.colorInfo}`,
        ...style,
      }}
    >
      <div
        style={{
          flex: '0 0 auto',
          width: 36,
          height: 36,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: token.colorInfoBorder,
          color: token.colorInfoActive,
          fontSize: 17,
        }}
      >
        <TranslationOutlined />
      </div>
      <div style={{ minWidth: 0 }}>
        <Typography.Text strong style={{ display: 'block' }}>
          {t('paperWorkLanguageNotice.title', { language: languageLabel })}
        </Typography.Text>
        <Typography.Text type="secondary">
          {t(`paperWorkLanguageNotice.${variant}`, { language: languageLabel })}
        </Typography.Text>
      </div>
    </div>
  );
};
